/**
 * useArTableClipboard
 *
 * Native copy/paste handlers for ArTable cells of any column type, mounted on
 * the table-body div beside useKeyboardNavigation. Mirrors that hook's
 * zero-subscription design: it takes uiState/schemaState/rowsState as
 * non-reactive refs and reads them via snapshot()/proxy at event time, calls no
 * useSnapshot, and so never re-renders when cells change — the governing
 * "no additional rerendering" constraint for SYD-2138.
 *
 * Copy serializes the focused cell (or the focused column across a multi-row
 * selection) to text/plain + a text/html marker table; formula columns copy
 * their computed value. Paste fills the focused column downward via
 * actions.pasteDownColumn, coercing per column type. A single external value
 * landing in a text input is left to the browser so mid-edit insertion still
 * works; an open Select popup is likewise left alone so pasting into search
 * works. Every branch below needs a focused cell inside the body div, and that
 * pair is the whole test for whether the grid owns the gesture: an editor
 * outside every cell — the row annotation — releases `cellFocus.focusedCell`
 * when focus or a right-click reaches it, so its paste falls through to the
 * browser and the row menu's cell actions find no target (SYD-2955). An
 * overlay rendered through a portal — the workflow parameter panel, a
 * confirmation modal, the column filter menu — keeps that focused cell yet
 * sits outside the body div in the DOM while React still routes its paste
 * here, so onCopy and onPaste both stand down unless `e.currentTarget` holds
 * the focused element (SYD-2992) — for copy that is the only test, because a
 * caret in an empty panel field selects nothing and would otherwise put the
 * focused cell on the clipboard over what the user had copied.
 * onContextMenuCapture focuses the right-clicked cell so
 * the row context menu's cell actions target it.
 *
 * Rows (SYD-2314). `copyRowsToClipboard` and `pasteRowsFromClipboard` are the
 * menu-and-chord pair: both fire without a ClipboardEvent, so they go through
 * `navigator.clipboard` instead of `e.clipboardData`. The names say clipboard
 * because `actions.pasteRows` — the one that writes documents — sits one call
 * below. A row payload reaching the native `onPaste` is routed the same way:
 * `resolveRowPaste` matches its columns by `typeId` and `actions.pasteRows`
 * inserts. That branch is deliberately not gated on the focused column being
 * pasteable: the focused cell supplies only the anchor row. The whole-table
 * selection scope copies nothing, because the ticks under it are a declaration
 * rather than a list of rows.
 *
 * `copyRowsToClipboard` takes its scope explicitly — a row id, `'focusedRow'` or
 * `'ticked'` — rather than falling back to the focused row, so the bulk menu,
 * which is defined entirely by ticks, cannot copy a row nobody ticked.
 */

import { useCallback, useContext } from 'react';
import { snapshot } from 'valtio';
import { MessageContext } from '@/providers/MessageContext';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import { isOverlayOpen, parseCellIdFromElement } from '@/keyboard';
import {
  buildCopyPayload,
  buildRowCopyPayload,
  parseClipboard,
  pasteSummary,
  resolveColumn,
} from './arCellClipboard';
import type { ArCellClipboardEnvelope } from './arCellClipboard';
import { computePasteValues } from './coerceToColumnValue';
import { readSystemClipboard } from './readSystemClipboard';
import { writeSystemClipboard } from './writeSystemClipboard';
import { resolveRowPaste } from './resolveRowPaste';
import { createArFormulaEngineConfig } from '#types';
import type { ArRowData, ArRowId, ArTableType, TypeId } from '#types/System.Artifacts.types';
import type { CollectionState, DocumentState } from '@/store';
import type { Actions } from '../actions/useArTableActions';
import type { ArTableUIState } from '../state/createArTableState';

type UseArTableClipboardParams = {
  uiState: ArTableUIState;
  schemaState: DocumentState<ArTableType>;
  rowsState: CollectionState<ArRowData>;
  actions: Actions;
  disabled: boolean;
};

/**
 * Which rows a copy takes. A row id and `'focusedRow'` both name an anchor and
 * widen to the ticks when the ticks hold it; `'ticked'` names no anchor and
 * takes the ticks whole. `ArRowId` is a bare `string`, so the union documents
 * the three cases rather than discriminating them at the type level.
 */
type ArCopyRowsScope = ArRowId | 'focusedRow' | 'ticked';

type UseArTableClipboardReturn = {
  onCopy: (e: React.ClipboardEvent) => void;
  onPaste: (e: React.ClipboardEvent) => void;
  onContextMenuCapture: (e: React.MouseEvent) => void;
  /** Copies whole rows to the system clipboard, over the scope the caller names. */
  copyRowsToClipboard: (scope: ArCopyRowsScope) => Promise<void>;
  /** Inserts whole rows below the anchor, reading the payload off the system clipboard. */
  pasteRowsFromClipboard: (anchorRowId?: ArRowId) => Promise<void>;
};

function useArTableClipboard(params: UseArTableClipboardParams): UseArTableClipboardReturn {
  const { uiState, schemaState, rowsState, actions, disabled } = params;
  const { messageApi } = useContext(MessageContext);
  const { fieldTypes } = useContext(FieldTypeContext);

  const onCopy = useCallback((e: React.ClipboardEvent): void => {
    if (disabled || e.defaultPrevented) return;

    const activeEl = document.activeElement;
    if (!(activeEl instanceof HTMLElement)) return;
    if (!e.currentTarget.contains(activeEl)) return;

    const sel = window.getSelection();
    if (sel && !sel.isCollapsed && sel.toString().length > 0) return;
    if (
      (activeEl instanceof HTMLInputElement || activeEl instanceof HTMLTextAreaElement) &&
      activeEl.selectionStart != null &&
      activeEl.selectionEnd != null &&
      activeEl.selectionStart !== activeEl.selectionEnd
    ) {
      return;
    }

    const uiSnap = snapshot(uiState);
    const focused = uiSnap.cellFocus.focusedCell;
    if (!focused) return;

    const columnId = focused.columnId as TypeId;
    const target = resolveColumn(schemaState.data, fieldTypes, columnId);
    if (!target) return;

    const { plain, html } = buildCopyPayload({
      focused: { rowId: focused.rowId as ArRowId, columnId },
      selection: uiSnap.selection,
      rendered: (uiSnap.renderedRowIds ?? []) as ArRowId[],
      target,
      getRowData: (rowId) => rowsState.docs.get(rowId)?.data,
      formulaConfig: createArFormulaEngineConfig(schemaState.data ?? undefined),
    });
    e.preventDefault();
    e.clipboardData.setData('text/plain', plain);
    e.clipboardData.setData('text/html', html);
  }, [disabled, uiState, schemaState, rowsState, fieldTypes]);

  const copyRowsToClipboard = useCallback(async (scope: ArCopyRowsScope): Promise<void> => {
    if (disabled) return;

    const uiSnap = snapshot(uiState);
    if (uiSnap.selection.scope === 'all') {
      messageApi.info('Copy takes the rows you ticked. Tick the rows you want, then copy.');
      return;
    }

    const rendered = (uiSnap.renderedRowIds ?? []) as ArRowId[];
    const selectedRows = uiSnap.selection.selectedRows;
    const tickedInOrder = rendered.filter((rowId) => (selectedRows[rowId] ?? false) === true);

    /**
     * Matches `buildCopyPayload`: the ticks when they hold the anchor, else the
     * anchor alone. `'ticked'` skips the widening because the ticks ARE the
     * scope, and an unresolvable `'focusedRow'` copies nothing rather than
     * silently becoming a ticked copy.
     */
    const scopeRowIds = ((): ArRowId[] => {
      if (scope === 'ticked') return tickedInOrder;
      const anchor = scope === 'focusedRow'
        ? (uiSnap.cellFocus.focusedCell?.rowId as ArRowId | undefined)
        : scope;
      if (anchor === undefined) return [];
      const ticksHoldAnchor = (selectedRows[anchor] ?? false) === true;
      return uiSnap.selection.selectedCount > 1 && ticksHoldAnchor && tickedInOrder.length > 0
        ? tickedInOrder
        : [anchor];
    })();

    if (scopeRowIds.length === 0) {
      messageApi.info('Select a row to copy.');
      return;
    }

    const { plain, html } = buildRowCopyPayload({
      rowIds: scopeRowIds,
      schemaData: schemaState.data,
      fieldTypes,
      getRowData: (rowId) => rowsState.docs.get(rowId)?.data,
      formulaConfig: createArFormulaEngineConfig(schemaState.data ?? undefined),
    });

    messageApi.info(
      (await writeSystemClipboard({ plain, html }))
        ? `Copied ${scopeRowIds.length} ${scopeRowIds.length === 1 ? 'row' : 'rows'}`
        : 'Clipboard access was blocked.'
    );
  }, [disabled, uiState, schemaState, rowsState, fieldTypes, messageApi]);

  /**
   * `affected` is left at zero on purpose: a row paste lands exactly the rows it
   * created, so quoting both would count them twice in one sentence. `skipped`
   * quotes the matcher's CELL drops and nothing else — `actions.pasteRows`
   * counts ROWS, and adding the two together said "skipped 4" for one dropped
   * row of four columns. Its refusals ride in `rejected` instead.
   *
   * An off-screen anchor is reported before the counts: the action wrote nothing
   * and attempted nothing, so a summary there would describe a paste that never
   * ran.
   */
  const applyRowPaste = useCallback(async (
    anchorRowId: ArRowId,
    rows: readonly (readonly ArCellClipboardEnvelope[])[]
  ): Promise<void> => {
    const resolved = resolveRowPaste({ rows, schemaData: schemaState.data, fieldTypes });
    if (resolved.grid.length === 0) {
      messageApi.info('Nothing to paste.');
      return;
    }
    const result = await actions.pasteRows(anchorRowId, resolved.grid);
    if (result.failed) return;
    if (!result.anchorOnScreen) {
      messageApi.info('Scroll to the row you want to paste below, then paste.');
      return;
    }
    const summary = pasteSummary({
      affected: 0,
      skipped: resolved.skipped,
      created: result.created,
      derived: resolved.derived,
      rejected: result.rejected,
    });
    if (summary) messageApi.info(summary);
  }, [actions, fieldTypes, messageApi, schemaState]);

  const pasteRowsFromClipboard = useCallback(async (anchorRowId?: ArRowId): Promise<void> => {
    if (disabled) return;

    const anchor = anchorRowId
      ?? (snapshot(uiState).cellFocus.focusedCell?.rowId as ArRowId | undefined);
    if (!anchor) {
      messageApi.info('Select a row to paste below.');
      return;
    }

    const payload = await readSystemClipboard();
    if (!payload) {
      messageApi.info('Clipboard access was blocked.');
      return;
    }

    const parsed = parseClipboard(payload);
    if (parsed.kind !== 'structuredRows') {
      messageApi.info('Copy rows first, then paste them.');
      return;
    }

    await applyRowPaste(anchor, parsed.rows);
  }, [applyRowPaste, disabled, messageApi, uiState]);

  const onPaste = useCallback((e: React.ClipboardEvent): void => {
    if (disabled || e.defaultPrevented) return;

    const activeEl = document.activeElement;
    if (!(activeEl instanceof HTMLElement)) return;
    if (!e.currentTarget.contains(activeEl)) return;
    if (isOverlayOpen(activeEl)) return;

    const uiSnap = snapshot(uiState);
    const focused = uiSnap.cellFocus.focusedCell;
    if (!focused) return;

    const parsed = parseClipboard(e.clipboardData);

    /**
     * Ahead of the column checks, because a row paste asks nothing of the focused
     * column beyond which row it sits on: `!target.pasteable` would swallow a row
     * payload dropped on a formula or protected cell, and the two guards below
     * both answer questions only a cell paste raises.
     */
    if (parsed.kind === 'structuredRows') {
      e.preventDefault();
      void applyRowPaste(focused.rowId as ArRowId, parsed.rows);
      return;
    }

    const columnId = focused.columnId as TypeId;
    const target = resolveColumn(schemaState.data, fieldTypes, columnId);
    if (!target || !target.pasteable) return;

    if (target.kind === 'reference' && !target.allowManual && parsed.kind === 'external') {
      e.preventDefault();
      messageApi.info('This column does not allow manual entry.');
      return;
    }

    const isTextInput = activeEl instanceof HTMLInputElement || activeEl instanceof HTMLTextAreaElement;
    const isSearchInput = activeEl.classList.contains('ant-select-selection-search-input');
    if (isTextInput && !isSearchInput && parsed.kind === 'external' && parsed.lines.length <= 1) {
      return;
    }

    const values = computePasteValues(parsed, target);
    if (values.length === 0) return;

    e.preventDefault();

    void actions
      .pasteDownColumn(focused.rowId as ArRowId, columnId, values, { overflow: 'createRows' })
      .then((result) => {
        const summary = pasteSummary(result);
        if (summary) messageApi.info(summary);
      });
  }, [applyRowPaste, disabled, uiState, schemaState, actions, messageApi, fieldTypes]);

  const onContextMenuCapture = useCallback((e: React.MouseEvent): void => {
    if (disabled) return;
    const targetEl = e.target;
    if (!(targetEl instanceof HTMLElement)) return;
    const parsed = parseCellIdFromElement(targetEl);
    if (!parsed) return;
    const current = uiState.cellFocus.focusedCell;
    if (current && current.rowId === parsed.rowId && current.columnId === parsed.colId) return;
    uiState.cellFocus.focusedCell = { rowId: parsed.rowId, columnId: parsed.colId };
  }, [disabled, uiState]);

  return { onCopy, onPaste, onContextMenuCapture, copyRowsToClipboard, pasteRowsFromClipboard };
}

export { useArTableClipboard };
export type { ArCopyRowsScope, UseArTableClipboardParams, UseArTableClipboardReturn };
